/**
 * After a keep-originals Fix run started from a playlist, swap each produced
 * file into the source playlist in place of its original (converted FLAC →
 * .wav, incompatible WAV → "[fixed]" copy) — same product rule as the DJ-
 * library sync, which already repoints Rekordbox/Serato to the produced file.
 *
 * Only the derived-copy case needs this: an in-place fix keeps the track
 * row's id (registerRename), so every playlist — local or cloud-linked —
 * follows the file automatically.
 *
 * Cloud playlists: repoint the LOCAL link (cloud_track_links row → local
 * track by path). The server-side origin path is fill-if-NULL by design and
 * stays stale — harmless, the link table wins on this machine and other
 * devices don't have the produced file anyway.
 *
 * Local playlists: remove the old row / insert the produced file at the same
 * position (positions are stable — no renumbering on remove).
 *
 * Best-effort per row. Returns the swaps that succeeded, in the same
 * {oldPath, newPath, rowId} shape — invert them (swap oldPath/newPath) and
 * call again to undo.
 */
import { refreshAndRetry } from '../redux/utils/refreshAndRetry';

export async function repointPlaylistRows({ source, playlistId, id, accessToken, swaps }) {
    const applied = [];
    // The tabs' playlistRef carries the playlist as `id`; accept both names —
    // the mismatch once made every commit no-op and warn "still points to
    // the original file" for every playlist.
    if (playlistId == null) playlistId = id;
    if (!playlistId || !swaps?.length) return applied;

    if (source === 'cloud') {
        // The load-time rowId map can be stale or absent (playlist loaded in
        // an older session, files list restored from Redux). Re-resolve any
        // missing rowIds fresh so the swap never silently no-ops.
        if (accessToken && swaps.some((s) => !s.rowId)) {
            try {
                const { loadCloudPlaylistFiles } = await import('./cloudPlaylistTracks');
                const fresh = await loadCloudPlaylistFiles(accessToken, playlistId);
                for (const swap of swaps) {
                    if (!swap.rowId) swap.rowId = fresh?.rowIdsByPath?.[swap.oldPath] || null;
                }
            } catch (_) { /* keep whatever rowIds we have */ }
        }
        for (const swap of swaps) {
            if (!swap.rowId) continue;
            try {
                const res = await window.electron.db.cloudLinks.setByPath(swap.rowId, swap.newPath);
                if (res?.linked) {
                    applied.push(swap);
                    // Also replace the server-side origin path (explicit
                    // replace — this is a deliberate file replacement, not a
                    // backfill). Without it a fresh install / second machine,
                    // which has no link table, would fall back to the stale
                    // original path and resurrect the pre-fix file. Best-effort:
                    // an old server ignores `replace` and keeps the stale path,
                    // which only this fallback path ever reads.
                    if (accessToken && window.electron.library?.setPlaylistTrackFilePath) {
                        refreshAndRetry((token) => window.electron.library.setPlaylistTrackFilePath(
                            token, playlistId, swap.rowId, swap.newPath, { replace: true }
                        )).catch(() => {});
                    }
                }
            } catch (_) { /* best-effort — leave this row on the original */ }
        }
        return applied;
    }

    // Local playlist: look up each old row's position once, then swap in place.
    let rows = [];
    try {
        rows = await window.electron.db.playlists.getTracks(playlistId) || [];
    } catch (_) {
        return applied;
    }
    const positionByPath = new Map(rows.map((r) => [r.file_path || r.file || r.filePath, r.position]));
    for (const swap of swaps) {
        if (!positionByPath.has(swap.oldPath)) continue;
        try {
            // Add first, remove second: a failure mid-swap leaves BOTH rows
            // (annoying) rather than silently dropping the track (data loss).
            await window.electron.db.playlists.addTrack(playlistId, swap.newPath, positionByPath.get(swap.oldPath));
            await window.electron.db.playlists.removeTrack(playlistId, swap.oldPath);
            applied.push(swap);
        } catch (_) { /* best-effort — leave this row on the original */ }
    }
    return applied;
}

/** The reverse operation for an Undo affordance. */
export function invertSwaps(swaps) {
    return swaps.map((s) => ({ ...s, oldPath: s.newPath, newPath: s.oldPath }));
}
